// Redaction for request/response logging. Logger writes to console (CloudWatch on the
// server) and to window.WD.logList on clients, so nothing that authenticates a user may
// reach it: bearer tokens, passwords, Magento customer/admin tokens.

const SENSITIVE_KEY = /pass(word)?|token|secret|authorization|api[_-]?key|cvv|cvc|card_?number|confirmation/i;
// Endpoints whose response body is itself a credential (Magento returns the token as a bare string).
// Matched against the path only, so a search term containing "login" doesn't hide a response.
const CREDENTIAL_RESPONSE_PATH = /token|login/i;
// Credentials Magento takes as path segments: password reset links and account confirmation keys.
const CREDENTIAL_PATH_SEGMENTS = [/(\/resetLinkToken\/)[^/?#]+/gi, /(\/account\/confirm\/[^/?#]+\/)[^/?#]+/gi];
const REDACTED = '[REDACTED]';
const MAX_DEPTH = 8;

export const maskAuthorization = (authorization?: string): string | undefined => {
  if (!authorization) {
    return authorization;
  }
  const value = authorization.replace(/^Bearer\s+/i, '');
  const tail = value.length > 8 ? value.slice(-4) : '';
  return `Bearer ***${tail}`;
};

export const redactForLog = (value: any, depth = 0): any => {
  if (value === null || value === undefined || typeof value !== 'object') {
    return value;
  }
  if (depth >= MAX_DEPTH) {
    return '[TRUNCATED]';
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactForLog(item, depth + 1));
  }
  const result: {[key: string]: any} = {};
  Object.keys(value).forEach((key) => {
    result[key] = SENSITIVE_KEY.test(key) ? REDACTED : redactForLog(value[key], depth + 1);
  });
  return result;
};

const SENSITIVE_PARAM = /token|pass|secret|key|auth|signature|confirmation/i;

// Decode one query-parameter NAME until stable, so %74oken (or %2574oken) is still seen as token.
// Per name, never the whole URL: decoding first would turn a value's %26 into a fake separator,
// and one malformed % elsewhere would stop the decoding for everything.
const decodeName = (name: string): string => {
  let result = name;
  for (let i = 0; i < 3; i++) {
    let next: string;
    try {
      next = decodeURIComponent(result);
    } catch (e) {
      return result;
    }
    if (next === result) {
      return result;
    }
    result = next;
  }
  return result;
};

export const redactUrlForLog = (url: string): string => {
  if (typeof url !== 'string') {
    return url;
  }
  const hashIndex = url.indexOf('#');
  const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const queryIndex = withoutHash.indexOf('?');
  let path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  for (const pattern of CREDENTIAL_PATH_SEGMENTS) {
    path = path.replace(pattern, `$1${REDACTED}`);
  }
  if (queryIndex < 0) {
    return path;
  }
  const query = withoutHash
    .slice(queryIndex + 1)
    .split('&')
    .map((param) => {
      const eq = param.indexOf('=');
      if (eq < 0) {
        return param;
      }
      const rawName = param.slice(0, eq);
      return SENSITIVE_PARAM.test(decodeName(rawName)) ? `${rawName}=${REDACTED}` : param;
    })
    .join('&');
  return `${path}?${query}`;
};

export const redactResponseForLog = (url: string, response: any): any =>
  CREDENTIAL_RESPONSE_PATH.test(url.split('?')[0]) ? REDACTED : redactForLog(response);

const scrubAxiosConfig = (config: any): any => {
  if (!config || typeof config !== 'object') {
    return config;
  }
  const headers = config.headers ? {...config.headers} : undefined;
  if (headers) {
    Object.keys(headers).forEach((key) => {
      if (/authorization/i.test(key)) {
        headers[key] = maskAuthorization(headers[key]);
      }
    });
  }
  return {
    ...config,
    url: typeof config.url === 'string' ? redactUrlForLog(config.url) : config.url,
    headers,
    data: config.data !== undefined ? REDACTED : undefined,
  };
};

// Axios errors carry the original request in e.config, e.request, e.response.config and
// e.response.request: bearer header, raw body (passwords) and URL (reset/confirm keys). Callers
// print rethrown errors as they are, so scrub the error once before it leaves RestServiceHandler.
// The request has already been sent; e.message, e.code, e.response.status and e.response.data
// stay intact for callers.
export const scrubErrorCredentials = (e: any): any => {
  if (e === null || e === undefined || typeof e !== 'object') {
    return e;
  }
  try {
    if (e.config) {
      e.config = scrubAxiosConfig(e.config);
    }
    if (e.request) {
      e.request = undefined;
    }
    if (e.response && typeof e.response === 'object') {
      e.response = {...e.response, config: scrubAxiosConfig(e.response.config), request: undefined};
    }
    return e;
  } catch (ignored) {
    // Frozen or otherwise unwritable: rethrow a clean copy rather than the original.
    const safe: any = new Error(e.message);
    safe.code = e.code;
    if (e.response) {
      safe.response = {status: e.response.status, data: e.response.data, headers: e.response.headers};
    }
    return safe;
  }
};

// Log only what is needed to diagnose a failed request.
export const summarizeErrorForLog = (url: string, e: any): any => {
  if (e === null || e === undefined || typeof e !== 'object') {
    return e;
  }
  return {
    message: e.message,
    code: e.code,
    status: e.response?.status,
    data: e.response ? redactResponseForLog(url, e.response.data) : undefined,
  };
};
